import Dexie, { Table } from 'dexie';
import { CoSyncDB, ICoSyncInfoDAOData } from '@/renderer/scripts/CoSync/SyncCenter/Repo/CoSyncDB';
import {
  CoSyncInfo,
  CoSyncStrategy,
  CoSyncTimingType,
} from '@/workerProcessRenderer/scripts/CoSyncEngine/common/CoSyncConfig';

/**
 *
 *
 * @export
 * @class CoSyncInfoDAO
 * 同步服务的配置DAO
 */
export default class CoSyncInfoDAO {
  private userInfo: CoFile.UserInfo;
  private db: CoSyncDB;

  constructor(userInfo: CoFile.UserInfo, db: CoSyncDB) {
    this.userInfo = userInfo;
    this.db = db;
  }

  public async add(info: CoSyncInfo): Promise<number> {
    const { userId } = this.userInfo;
    if (!userId) {
      throw { code: 'InvalidUserId' };
    }
    const orgId = this.userInfo.organizationId;
    if (!orgId) {
      throw { code: 'InvalidOrgId' };
    }

    const record: ICoSyncInfoDAOData = {
      localSyncId: info.localSyncId,
      remoteSyncId: info.syncId,
      userId,
      orgId,
      localPath: info.config.syncPath.localPath,
      remotePath: info.config.syncPath.remotePath,
      spaceId: info.config.syncPath.spaceId,
      spaceTag: info.config.syncPath.spaceTag,
      spaceOrgId: info.config.syncPath.spaceOrgId,
      teamId: info.config.syncPath.teamId,
      teamPath: info.config.syncPath.teamPath,
      isCreateBaseLocal: info.config.syncPath.isCreateBaseLocal,
      strategy: info.config.syncSetting.strategy,
      timingType: info.config.syncSetting.timingType,
      conflictStrategy: info.config.syncSetting.conflictStrategy,
      keepWhichLastStatus: info.config.syncSetting.keepWhichLastStatus,
      ignoreList: info.config.syncSetting.ignoreList ?? [],
    };
    return this.db.config.add(record);
  }

  public async remove(localSyncId: number): Promise<void> {
    return this.db.config.delete(localSyncId);
  }

  public async removeAll(): Promise<void> {
    return this.db.config.clear();
  }

  public async getAllConfigFromAllUser(): Promise<CoSyncInfo[]> {
    return (await this.db.config.toArray()).map(it => {
      const serviceIdAndConfig: CoSyncInfo = {
        syncId: it.remoteSyncId,
        localSyncId: it.localSyncId,
        config: {
          syncPath: {
            localPath: it.localPath,
            remotePath: it.remotePath,
            spaceId: it.spaceId,
            spaceTag: it.spaceTag,
            spaceOrgId: it.spaceOrgId,
            teamId: it.teamId,
            teamPath: it.teamPath,

            isCreateBaseLocal: it.isCreateBaseLocal,
          },
          syncSetting: {
            strategy: it.strategy,
            timingType: it.timingType,
            conflictStrategy: it.conflictStrategy,
            keepWhichLastStatus: it.keepWhichLastStatus,
            ignoreList: it.ignoreList ?? [],
          },
        },
      };

      return serviceIdAndConfig;
    });
  }

  public async getAllConfigFromCurrentUser(): Promise<CoSyncInfo[]> {
    const { userId } = this.userInfo;
    if (!userId) {
      throw { code: 'InvalidUserId' };
    }

    const orgId = this.userInfo.organizationId;
    if (!orgId) {
      throw { code: 'InvalidOrgId' };
    }

    return (await this.db.config.where({ userId, orgId }).toArray()).map(it => {
      const serviceIdAndConfig: CoSyncInfo = {
        syncId: it.remoteSyncId,
        localSyncId: it.localSyncId,
        config: {
          syncPath: {
            localPath: it.localPath,
            remotePath: it.remotePath,
            spaceId: it.spaceId,
            spaceTag: it.spaceTag,
            spaceOrgId: it.spaceOrgId,
            teamId: it.teamId,
            teamPath: it.teamPath,

            isCreateBaseLocal: it.isCreateBaseLocal,
          },
          syncSetting: {
            strategy: it.strategy,
            timingType: it.timingType,
            conflictStrategy: it.conflictStrategy,
            keepWhichLastStatus: it.keepWhichLastStatus,
            ignoreList: it.ignoreList ?? [],
          },
        },
      };

      return serviceIdAndConfig;
    });
  }

  public async update(info: CoSyncInfo): Promise<number> {
    const { userId } = this.userInfo;
    if (!userId) {
      throw { code: 'InvalidUserId' };
    }
    const orgId = this.userInfo.organizationId;
    if (!orgId) {
      throw { code: 'InvalidOrgId' };
    }

    const record: ICoSyncInfoDAOData = {
      localSyncId: info.localSyncId,
      remoteSyncId: info.syncId,
      userId,
      orgId,
      localPath: info.config.syncPath.localPath,
      remotePath: info.config.syncPath.remotePath,
      spaceId: info.config.syncPath.spaceId,
      spaceTag: info.config.syncPath.spaceTag,
      spaceOrgId: info.config.syncPath.spaceOrgId,
      teamId: info.config.syncPath.teamId,
      teamPath: info.config.syncPath.teamPath,
      isCreateBaseLocal: info.config.syncPath.isCreateBaseLocal,
      strategy: info.config.syncSetting.strategy,
      timingType: info.config.syncSetting.timingType,
      conflictStrategy: info.config.syncSetting.conflictStrategy,
      keepWhichLastStatus: info.config.syncSetting.keepWhichLastStatus,
      ignoreList: info.config.syncSetting.ignoreList ?? [],
    };
    return this.db.config.where('localSyncId').equals(record.localSyncId).modify(record);
  }
}
